import React, { useCallback, useEffect } from "react";
import { Modal, Box, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LikeButton from "../likebutton.js";

const ModalImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ModalImage = styled("img")({
  maxWidth: "90%",
  maxHeight: "90%",
  objectFit: "contain",
});

const ArrowButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 10,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "white",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 10,
  color: "white",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
}));

const ModalLikeButtonContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 10,
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  borderRadius: "50%",
  padding: 2,
}));

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  height: "80%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const ImageGalleryModal = ({
  open,
  handleClose,
  mediaImages,
  startIndex,
  imageSize,
  handlePrev,
  handleNext,
  currentUser,
  userObjectId,
  handleLikeChange,
  likeStateRef,
}) => {
  const currentImage = startIndex !== null && mediaImages[startIndex];
  const currentImageUrl = currentImage?.mediaUrl?.[imageSize];

  // Keyboard navigation within the modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (open) {
        console.log("ImageGalleryModal - Key down event:", event.key);
        if (event.key === "ArrowLeft") {
          handlePrev();
        } else if (event.key === "ArrowRight") {
          handleNext();
        } else if (event.key === "Escape") {
          handleClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handlePrev, handleNext, handleClose]);

  if (!open || !currentImage || !currentImageUrl) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...modalStyle, position: "relative" }}>
        <CloseButton aria-label="close" onClick={handleClose}>
          <CloseIcon style={{ color: "white" }} />
        </CloseButton>
        <ModalImageContainer>
          {mediaImages.length > 1 && (
            <ArrowButton
              aria-label="previous image"
              onClick={handlePrev}
              style={{ left: 10 }}
              disabled={mediaImages.length <= 1}
            >
              <ArrowBackIosIcon style={{ color: "white" }} />
            </ArrowButton>
          )}
          <a href={currentImageUrl} target="_blank" rel="noopener noreferrer">
            <ModalImage
              src={currentImageUrl}
              alt="scuba diving thematic imagery"
              loading="eager"
              decoding="async"
            />
          </a>
          {mediaImages.length > 1 && (
            <ArrowButton
              aria-label="next image"
              onClick={handleNext}
              style={{ right: 10 }}
              disabled={mediaImages.length <= 1}
            >
              <ArrowForwardIosIcon style={{ color: "white" }} />
            </ArrowButton>
          )}
          {currentUser && userObjectId && currentImage && (
            <ModalLikeButtonContainer>
              <LikeButton
                userId={userObjectId}
                itemId={currentImage._id}
                onLikeChange={handleLikeChange}
                initialLiked={
                  currentImage.liked ||
                  likeStateRef.current.get(currentImage._id) ||
                  false
                }
                key={`modal-like-${currentImage._id}-${currentImage.liked}`}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                  },
                }}
              />
            </ModalLikeButtonContainer>
          )}
        </ModalImageContainer>
      </Box>
    </Modal>
  );
};

export default ImageGalleryModal;
